import type {Channel, Connection, Content} from './types';

export type SenderKind = 'person' | 'organization';
export type PublishingDestination = {
  connectionId: string;
  resourceId: string;
  platform?: string;
  senderKind?: SenderKind;
  state?: string;
};

export function platformName(platform?: string) {
  return ({linkedin: 'LinkedIn', instagram: 'Instagram', facebook: 'Facebook'} as Record<string, string>)[platform || ''] || platform || 'Ukendt platform';
}

export function senderDisplayName(connection?: Connection) {
  const name = connection?.displayName;
  return name ? connection?.platform === 'instagram' ? `@${name.replace(/^@/, '')}` : name : 'Profil ikke tilgængelig';
}

export function senderKindLabel(connection?: Connection, channel?: Channel, frozenKind?: SenderKind, frozenPlatform?: string) {
  const platform = frozenPlatform || connection?.platform;
  if (platform === 'instagram') return connection?.instagramAccountType === 'BUSINESS' ? 'Virksomhedskonto' : connection?.instagramAccountType === 'CREATOR' ? 'Creator-konto' : 'Instagram-konto';
  if (platform === 'facebook') return 'Facebook-side';
  const kind = frozenKind || connection?.senderKind || channel?.resource.kind;
  return kind === 'person' ? 'Personlig profil' : kind === 'organization' ? 'Virksomhed' : 'Profiltype ukendt';
}

export function senderOptionLabel(connection?: Connection, channel?: Channel) {
  return `${platformName(connection?.platform)} · ${senderDisplayName(connection)} · ${senderKindLabel(connection, channel)}`;
}

/** Read destinations from the actual variants/receipts, never from the signed-in user. */
export function publishingDestinations(item: Content, published = false): PublishingDestination[] {
  const destinations = new Map<string, PublishingDestination>();
  const add = (destination: PublishingDestination) => destinations.set(`${destination.resourceId}:${destination.connectionId}`, destination);
  if (published) {
    for (const publication of item.publications || []) {
      add({resourceId: publication.resourceId, connectionId: publication.connectionId, state: 'Udgivet'});
    }
    return [...destinations.values()];
  }
  for (const variant of item.variants || []) {
    add({resourceId: variant.resourceId, connectionId: variant.connectionId, state: 'Kladde'});
  }
  // A failed historical attempt must not replace the state of an active schedule.
  const priority: Record<string, number> = {failed: 1, unknown: 2, queued: 3, scheduled: 3, claimed: 4, sending: 4};
  for (const job of [...(item.jobs || [])].sort((a, b) => (priority[a.status] || 0) - (priority[b.status] || 0))) {
    if (!['queued', 'scheduled', 'claimed', 'sending', 'unknown', 'failed'].includes(job.status)) continue;
    const snapshot = job.preview?.senderSnapshot;
    const variant = item.variants?.find(candidate => candidate.id === job.target.variantId);
    const connectionId = snapshot?.connectionId || job.target.connectionId || variant?.connectionId;
    if (!connectionId) continue;
    add({resourceId: snapshot?.resourceId || job.resourceId, connectionId, senderKind: snapshot?.senderKind, platform: snapshot?.platform || job.platform,
      state: job.status === 'unknown' ? 'Afventer afklaring' : job.status === 'failed' ? 'Mislykket' : ['claimed', 'sending'].includes(job.status) ? 'Sender' : 'Planlagt'});
  }
  return [...destinations.values()];
}
